<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import FunnelsAPI from 'dashboard/api/funnels';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  contactId: {
    type: Number,
    required: true,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const isAddingCard = ref(false);
const isMovingCard = ref(false);
const funnelItems = ref([]);
const funnels = ref([]);
const showAddModal = ref(false);
const showMoveModal = ref(false);
const selectedFunnel = ref(null);
const selectedStage = ref(null);
const stages = ref([]);
const itemToMove = ref(null);
const moveStages = ref([]);

// Options step (shown after stage selection)
const showOptionsStep = ref(false);
const pendingAction = ref(null); // 'add' or 'move'
const optionNewClient = ref(false);
const optionValue = ref('');
const optionDueDate = ref('');
const optionSaleClosed = ref(null);
const optionSaleDate = ref('');
const optionSaleLostReason = ref('');

const accountId = computed(() => store.getters.getCurrentAccountId);
const contact = computed(() =>
  store.getters['contacts/getContact'](props.contactId)
);

const fetchFunnelItems = async () => {
  if (!props.contactId) return;

  isLoading.value = true;
  try {
    const response = await FunnelsAPI.getContactItems(props.contactId);
    funnelItems.value = response.data;
  } catch (error) {
    console.error('Failed to fetch funnel items:', error);
  } finally {
    isLoading.value = false;
  }
};

const fetchFunnels = async () => {
  try {
    const response = await FunnelsAPI.get();
    funnels.value = response.data;
  } catch (error) {
    console.error('Failed to fetch funnels:', error);
  }
};

const fetchStages = async funnelId => {
  if (!funnelId) {
    stages.value = [];
    return;
  }
  try {
    const response = await FunnelsAPI.getStages(funnelId);
    stages.value = response.data;
  } catch (error) {
    console.error('Failed to fetch stages:', error);
  }
};

const openAddModal = async () => {
  showAddModal.value = true;
  await fetchFunnels();
  // Se tiver apenas 1 board, seleciona automaticamente
  if (funnels.value.length === 1) {
    selectedFunnel.value = funnels.value[0].id;
    await fetchStages(selectedFunnel.value);
  }
};

const closeAddModal = () => {
  showAddModal.value = false;
  selectedFunnel.value = null;
  selectedStage.value = null;
  stages.value = [];
};

const onFunnelChange = () => {
  selectedStage.value = null;
  if (selectedFunnel.value) {
    fetchStages(selectedFunnel.value);
  } else {
    stages.value = [];
  }
};

const hasSingleFunnel = computed(() => funnels.value.length === 1);

const resetOptions = () => {
  optionNewClient.value = false;
  optionValue.value = '';
  optionDueDate.value = '';
  optionSaleClosed.value = null;
  optionSaleDate.value = '';
  optionSaleLostReason.value = '';
};

const selectStageDirectly = stageId => {
  selectedStage.value = stageId;
  pendingAction.value = 'add';
  resetOptions();
  showOptionsStep.value = true;
};

const confirmWithOptions = async () => {
  if (pendingAction.value === 'add') {
    await addToFunnel();
  } else if (pendingAction.value === 'move') {
    await executeMoveToStage();
  }
};

const cancelOptionsStep = () => {
  showOptionsStep.value = false;
  pendingAction.value = null;
  optionNewClient.value = false;
};

const addToFunnel = async () => {
  if (!selectedFunnel.value || !selectedStage.value) {
    useAlert(t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_FUNNEL_STAGE'));
    return;
  }

  isAddingCard.value = true;
  try {
    const contactName =
      contact.value?.name ||
      contact.value?.phone_number ||
      `Contato #${props.contactId}`;
    const payload = {
      funnel_stage_id: selectedStage.value,
      contact_id: props.contactId,
      conversation_id: props.conversationId,
      title: contactName,
      value: optionValue.value ? parseFloat(optionValue.value) : null,
      due_date: optionDueDate.value || null,
      sale_closed: optionSaleClosed.value,
      sale_lost_reason: optionSaleClosed.value === false ? optionSaleLostReason.value : null,
      custom_attributes: {
        is_new_client: optionNewClient.value,
        sale_date: optionSaleClosed.value === true ? optionSaleDate.value : null,
      },
    };
    if (optionSaleClosed.value === true) payload.status = 'ganho';
    else if (optionSaleClosed.value === false) payload.status = 'perdido';
    await FunnelsAPI.createItem(selectedFunnel.value, payload);
    useAlert(t('CONTACT_PANEL.FUNNEL_ITEMS.CARD_ADDED'));
    showOptionsStep.value = false;
    closeAddModal();
    fetchFunnelItems();
  } catch (error) {
    const errorMsg =
      error.response?.data?.error || t('CONTACT_PANEL.FUNNEL_ITEMS.ADD_ERROR');
    useAlert(errorMsg);
  } finally {
    isAddingCard.value = false;
  }
};

const goToFunnel = item => {
  const route = `/app/accounts/${accountId.value}/funnels/${item.funnel_id}`;
  window.open(route, '_blank');
};

const removeFromFunnel = async item => {
  if (!confirm(t('CONTACT_PANEL.FUNNEL_ITEMS.CONFIRM_REMOVE'))) return;

  try {
    await FunnelsAPI.deleteItem(item.funnel_id, item.id);
    useAlert(t('CONTACT_PANEL.FUNNEL_ITEMS.CARD_REMOVED'));
    fetchFunnelItems();
  } catch (error) {
    useAlert(t('CONTACT_PANEL.FUNNEL_ITEMS.REMOVE_ERROR'));
  }
};

// Move stage functions
const openMoveModal = async item => {
  itemToMove.value = item;
  showMoveModal.value = true;
  try {
    const response = await FunnelsAPI.getStages(item.funnel_id);
    moveStages.value = response.data.filter(
      stage => stage.id !== item.stage_id
    );
  } catch (error) {
    console.error('Failed to fetch stages for move:', error);
    moveStages.value = [];
  }
};

const closeMoveModal = () => {
  showMoveModal.value = false;
  itemToMove.value = null;
  moveStages.value = [];
};

const moveToStage = stageId => {
  selectedStage.value = stageId;
  pendingAction.value = 'move';
  const item = itemToMove.value;
  optionNewClient.value = item?.custom_attributes?.is_new_client || false;
  optionValue.value = item?.value || '';
  optionDueDate.value = item?.due_date ? item.due_date.split('T')[0] : '';
  optionSaleClosed.value = item?.sale_closed ?? null;
  optionSaleDate.value = item?.custom_attributes?.sale_date || '';
  optionSaleLostReason.value = item?.sale_lost_reason || '';
  showOptionsStep.value = true;
};

const executeMoveToStage = async () => {
  if (!itemToMove.value || !selectedStage.value) return;

  isMovingCard.value = true;
  try {
    // Move the item to the new stage
    await FunnelsAPI.moveItem(
      itemToMove.value.funnel_id,
      itemToMove.value.id,
      selectedStage.value
    );
    // Update item fields
    const updatePayload = {
      due_date: optionDueDate.value || null,
      sale_closed: optionSaleClosed.value,
      sale_lost_reason: optionSaleClosed.value === false ? optionSaleLostReason.value : null,
      custom_attributes: {
        is_new_client: optionNewClient.value,
        sale_date: optionSaleClosed.value === true ? optionSaleDate.value : null,
      },
    };
    // Só envia value se preenchido, para não zerar valor existente
    if (optionValue.value !== '' && optionValue.value !== null && optionValue.value !== undefined) {
      updatePayload.value = parseFloat(optionValue.value);
    }
    if (optionSaleClosed.value === true) updatePayload.status = 'ganho';
    else if (optionSaleClosed.value === false) updatePayload.status = 'perdido';
    else updatePayload.status = 'ativo';
    await FunnelsAPI.updateItem(
      itemToMove.value.funnel_id,
      itemToMove.value.id,
      updatePayload
    );
    useAlert(t('CONTACT_PANEL.FUNNEL_ITEMS.CARD_MOVED'));
    showOptionsStep.value = false;
    closeMoveModal();
    fetchFunnelItems();
  } catch (error) {
    useAlert(t('CONTACT_PANEL.FUNNEL_ITEMS.MOVE_ERROR'));
  } finally {
    isMovingCard.value = false;
  }
};

watch(
  () => props.contactId,
  newId => {
    if (newId) {
      fetchFunnelItems();
    }
  }
);

const shouldShowAddButton = computed(() => {
  if (funnelItems.value.length === 0) return true;
  // Se já tem item e só existe 1 funil, esconde o botão
  if (funnelItems.value.length > 0 && funnels.value.length === 1) return false;
  return true;
});

onMounted(async () => {
  await Promise.all([fetchFunnelItems(), fetchFunnels()]);
});
</script>

<template>
  <div class="contact-funnel-items">
    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center py-4">
      <Spinner size="small" />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="funnelItems.length === 0"
      class="text-sm text-n-slate-11 py-2"
    >
      {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.NO_ITEMS') }}
    </div>

    <!-- List of funnel items -->
    <div v-else class="flex flex-col gap-2">
      <div
        v-for="item in funnelItems"
        :key="item.id"
        class="flex items-center justify-between p-2 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors"
      >
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span
              class="w-2 h-2 rounded-full flex-shrink-0"
              :style="{ backgroundColor: item.stage_color || '#6366f1' }"
            />
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ item.funnel_title }}
            </span>
          </div>
          <div class="text-xs text-n-slate-11 mt-0.5 pl-4">
            {{ item.stage_name }}
          </div>
        </div>
        <div class="flex items-center gap-1">
          <button
            class="p-1 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-brand"
            :title="$t('CONTACT_PANEL.FUNNEL_ITEMS.MOVE_STAGE')"
            @click="openMoveModal(item)"
          >
            <fluent-icon icon="arrow-sort" size="14" />
          </button>
          <button
            class="p-1 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12"
            :title="$t('CONTACT_PANEL.FUNNEL_ITEMS.VIEW_BOARD')"
            @click="goToFunnel(item)"
          >
            <fluent-icon icon="open" size="14" />
          </button>
          <button
            class="p-1 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-ruby-11"
            :title="$t('CONTACT_PANEL.FUNNEL_ITEMS.REMOVE')"
            @click="removeFromFunnel(item)"
          >
            <fluent-icon icon="delete" size="14" />
          </button>
        </div>
      </div>
    </div>

    <!-- Add button (hidden when already in a board and only 1 funnel exists) -->
    <button
      v-if="shouldShowAddButton"
      class="flex items-center gap-1 mt-3 text-sm text-n-brand hover:text-n-brand-dark transition-colors"
      @click="openAddModal"
    >
      <fluent-icon icon="add" size="14" />
      {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.ADD_TO_BOARD') }}
    </button>

    <!-- Add Modal -->
    <woot-modal :show="showAddModal" :on-close="closeAddModal">
      <div class="p-6">
        <h3 class="text-lg font-medium text-n-slate-12 mb-4">
          {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.ADD_TO_BOARD') }}
        </h3>

        <div class="flex flex-col gap-4">
          <!-- Funnel select (só mostra se tiver mais de 1 board) -->
          <div v-if="!hasSingleFunnel">
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_BOARD') }}
            </label>
            <select
              v-model="selectedFunnel"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:border-n-brand focus:outline-none"
              @change="onFunnelChange"
            >
              <option :value="null">
                {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_PLACEHOLDER') }}
              </option>
              <option
                v-for="funnel in funnels"
                :key="funnel.id"
                :value="funnel.id"
              >
                {{ funnel.title }}
              </option>
            </select>
          </div>

          <!-- Etapas em lista (quando só tem 1 board) -->
          <div v-if="hasSingleFunnel && stages.length > 0">
            <label class="block text-sm font-medium text-n-slate-11 mb-2">
              {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_STAGE') }}
            </label>
            <div class="flex flex-col gap-1">
              <button
                v-for="stage in stages"
                :key="stage.id"
                class="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-2 hover:border-n-brand transition-colors text-left"
                :disabled="isAddingCard"
                @click="selectStageDirectly(stage.id)"
              >
                <span
                  class="w-3 h-3 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: stage.color || '#6366f1' }"
                />
                <span class="text-sm">{{ stage.name }}</span>
              </button>
            </div>
          </div>

          <!-- Stage select dropdown (quando tem mais de 1 board) -->
          <div v-if="!hasSingleFunnel">
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_STAGE') }}
            </label>
            <select
              v-model="selectedStage"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:border-n-brand focus:outline-none"
              :disabled="!selectedFunnel"
            >
              <option :value="null">
                {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_PLACEHOLDER') }}
              </option>
              <option v-for="stage in stages" :key="stage.id" :value="stage.id">
                {{ stage.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Botões só aparecem quando tem mais de 1 board -->
        <div v-if="!hasSingleFunnel" class="flex justify-end gap-2 mt-6">
          <woot-button variant="clear" @click="closeAddModal">
            {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.CANCEL') }}
          </woot-button>
          <woot-button
            :is-loading="isAddingCard"
            :disabled="!selectedFunnel || !selectedStage"
            @click="addToFunnel"
          >
            {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.ADD') }}
          </woot-button>
        </div>

        <!-- Botão cancelar quando tem 1 board -->
        <div v-else class="flex justify-end mt-4">
          <woot-button variant="clear" @click="closeAddModal">
            {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.CANCEL') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>

    <!-- Move Stage Modal -->
    <woot-modal :show="showMoveModal" :on-close="closeMoveModal">
      <div class="p-6">
        <h3 class="text-lg font-medium text-n-slate-12 mb-2">
          {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.MOVE_STAGE') }}
        </h3>
        <p v-if="itemToMove" class="text-sm text-n-slate-11 mb-4">
          {{ itemToMove.funnel_title }} - {{ itemToMove.stage_name }}
        </p>

        <div class="flex flex-col gap-4">
          <div v-if="moveStages.length > 0">
            <label class="block text-sm font-medium text-n-slate-11 mb-2">
              {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_NEW_STAGE') }}
            </label>
            <div class="flex flex-col gap-1">
              <button
                v-for="stage in moveStages"
                :key="stage.id"
                class="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-2 hover:border-n-brand transition-colors text-left cursor-pointer"
                :disabled="isMovingCard"
                @click="moveToStage(stage.id)"
              >
                <span
                  class="w-3 h-3 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: stage.color || '#6366f1' }"
                />
                <span class="text-sm">{{ stage.name }}</span>
              </button>
            </div>
          </div>

          <div v-else class="text-sm text-n-slate-11 py-4 text-center">
            {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.NO_OTHER_STAGES') }}
          </div>
        </div>

        <div class="flex justify-end mt-4">
          <woot-button variant="clear" @click="closeMoveModal">
            {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.CANCEL') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>

    <!-- Options Step Modal -->
    <woot-modal :show="showOptionsStep" :on-close="cancelOptionsStep">
      <div class="p-6">
        <h3 class="text-lg font-medium text-n-slate-12 mb-4">
          Opções do Card
        </h3>

        <div class="flex flex-col gap-4">
          <!-- Valor e Data do Orçamento -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-1">
                Valor (R$)
              </label>
              <input
                v-model="optionValue"
                type="number"
                placeholder="0,00"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-3 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-1">
                Data do Orçamento
              </label>
              <input
                v-model="optionDueDate"
                type="date"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>
          </div>

          <!-- Cliente Novo toggle -->
          <button
            type="button"
            class="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer"
            :class="
              optionNewClient
                ? 'border-n-brand bg-n-brand/10 text-n-brand'
                : 'border-n-weak bg-n-solid-3 text-n-slate-11 hover:border-n-brand/50'
            "
            @click="optionNewClient = !optionNewClient"
          >
            <span
              class="size-5 rounded flex items-center justify-center border-2 transition-all flex-shrink-0"
              :class="
                optionNewClient
                  ? 'border-n-brand bg-n-brand'
                  : 'border-n-slate-8'
              "
            >
              <span
                v-if="optionNewClient"
                class="i-lucide-check size-3 text-white"
              />
            </span>
            <span class="i-lucide-user-plus size-4 flex-shrink-0" />
            Cliente Novo
          </button>

          <!-- Fechou Venda -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              Fechou venda?
            </label>
            <div class="flex gap-3">
              <button
                type="button"
                class="flex-1 px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer"
                :class="
                  optionSaleClosed === true
                    ? 'border-n-jade-9 bg-n-jade-3 text-n-jade-11'
                    : 'border-n-weak bg-n-solid-3 text-n-slate-11 hover:border-n-jade-6'
                "
                @click="
                  optionSaleClosed === true
                    ? ((optionSaleClosed = null), (optionSaleDate = ''))
                    : ((optionSaleClosed = true),
                      (optionSaleLostReason = ''),
                      (optionSaleDate =
                        optionSaleDate ||
                        new Date().toISOString().split('T')[0]))
                "
              >
                <span class="i-lucide-check size-4 inline-block mr-1" />
                Sim
              </button>
              <button
                type="button"
                class="flex-1 px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer"
                :class="
                  optionSaleClosed === false
                    ? 'border-n-ruby-9 bg-n-ruby-3 text-n-ruby-11'
                    : 'border-n-weak bg-n-solid-3 text-n-slate-11 hover:border-n-ruby-6'
                "
                @click="
                  optionSaleClosed === false
                    ? ((optionSaleClosed = null), (optionSaleLostReason = ''))
                    : (optionSaleClosed = false)
                "
              >
                <span class="i-lucide-x size-4 inline-block mr-1" />
                Não
              </button>
            </div>

            <!-- Data da Venda -->
            <div
              v-if="optionSaleClosed === true"
              class="mt-3 p-3 rounded-lg bg-n-jade-2 border-2 border-n-jade-6"
            >
              <label class="block text-sm font-medium text-n-jade-11 mb-1.5">
                <span
                  class="i-lucide-calendar-check size-4 inline-block mr-1"
                />
                Data da Venda
              </label>
              <input
                v-model="optionSaleDate"
                type="date"
                class="w-full px-3 py-2 rounded-lg border border-n-jade-6 bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-jade-9"
              />
            </div>

            <!-- Motivo da perda -->
            <div
              v-if="optionSaleClosed === false"
              class="mt-3 p-3 rounded-lg bg-n-ruby-2 border border-n-ruby-6"
            >
              <label class="block text-sm font-medium text-n-ruby-11 mb-1.5">
                Motivo da perda
              </label>
              <textarea
                v-model="optionSaleLostReason"
                class="w-full px-3 py-2 rounded-lg border border-n-ruby-6 bg-n-solid-3 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-ruby-9 resize-none"
                rows="2"
                placeholder="Descreva o motivo da perda..."
              />
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-5">
          <woot-button variant="clear" class="cursor-pointer" @click="cancelOptionsStep">
            Voltar
          </woot-button>
          <woot-button
            class="cursor-pointer"
            :is-loading="isAddingCard || isMovingCard"
            @click="confirmWithOptions"
          >
            Confirmar
          </woot-button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>
